// @figma file="components-web" component="FileUploadZone"
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckCircle2, CloudUpload, Loader2, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

export const fileUploadZoneVariants = cva(
  [
    'relative flex flex-col items-center justify-center gap-2',
    'rounded-[var(--radius)] border-2 border-dashed',
    'px-6 py-10 text-center',
    'transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'cursor-pointer select-none',
  ],
  {
    variants: {
      isDragging: {
        true: 'border-primary bg-primary/5',
        false: 'border-border bg-muted/30 hover:bg-muted/50 hover:border-muted-foreground/40',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed pointer-events-none',
        false: '',
      },
    },
    defaultVariants: {
      isDragging: false,
      disabled: false,
    },
  },
);

// ─── Types ────────────────────────────────────────────────────────────────────

type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

interface FileEntry {
  file: File;
  objectUrl: string | null;
  status: UploadStatus;
  uploadedKey?: string;
}

export interface FileUploadZoneProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof fileUploadZoneVariants> {
  /** What files to accept (e.g. "image/*", ".pdf,image/jpeg") */
  accept?: string;
  /** Max file size in MB (default: 25) */
  maxSizeMb?: number;
  /** Allow multiple files (default: false) */
  multiple?: boolean;
  /** Controlled value — array of selected files */
  value?: File[];
  /** Called whenever the file list changes */
  onChange?: (files: File[]) => void;
  /** Per-file upload callback — returns the upload URL/key on success (presigned S3 flow) */
  onUpload?: (file: File) => Promise<string>;
  /** Show image thumbnail preview (for image/* accepts) */
  imageMode?: boolean;
  /** Disabled state */
  disabled?: boolean;
  className?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function bytesToMb(bytes: number): number {
  return bytes / (1024 * 1024);
}

/**
 * Returns true when `file` matches one of the comma-separated accept patterns.
 * Handles MIME types (e.g. "image/*", "image/jpeg") and extensions (e.g. ".pdf").
 */
function fileMatchesAccept(file: File, accept: string): boolean {
  const patterns = accept
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (patterns.length === 0) return true;

  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) {
      return file.name.toLowerCase().endsWith(pattern.toLowerCase());
    }
    if (pattern.endsWith('/*')) {
      const baseType = pattern.slice(0, -2);
      return file.type.startsWith(`${baseType}/`);
    }
    return file.type === pattern;
  });
}

// ─── FileUploadZone ───────────────────────────────────────────────────────────

/**
 * Drag-and-drop file upload zone with optional per-file upload callback,
 * inline validation errors, image thumbnail previews, and upload progress states.
 *
 * @example Basic usage
 * ```tsx
 * <FileUploadZone accept=".pdf,image/*" onChange={(files) => console.log(files)} />
 * ```
 *
 * @example With presigned S3 upload
 * ```tsx
 * <FileUploadZone
 *   accept="image/*"
 *   imageMode
 *   onUpload={async (file) => {
 *     const { url, key } = await getPresignedUrl(file.name);
 *     await fetch(url, { method: 'PUT', body: file });
 *     return key;
 *   }}
 * />
 * ```
 */
const FileUploadZone = React.forwardRef<HTMLDivElement, FileUploadZoneProps>(
  (
    {
      accept,
      maxSizeMb = 25,
      multiple = false,
      value,
      onChange,
      onUpload,
      imageMode = false,
      disabled = false,
      className,
      ...props
    },
    ref,
  ) => {
    // ── State ──────────────────────────────────────────────────────────────
    const [isDragging, setIsDragging] = React.useState(false);
    const [entries, setEntries] = React.useState<FileEntry[]>([]);
    const [errors, setErrors] = React.useState<string[]>([]);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dragCounterRef = React.useRef(0);

    // ── Object URL cleanup ─────────────────────────────────────────────────
    React.useEffect(() => {
      return () => {
        for (const entry of entries) {
          if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
        }
      };
      // Only run on unmount — we manage individual revocation elsewhere
    }, []);

    // ── Sync controlled value ──────────────────────────────────────────────
    // When `value` changes externally (e.g. form reset), rebuild the entries list.
    const prevValueRef = React.useRef<File[] | undefined>(value);
    React.useEffect(() => {
      if (value === prevValueRef.current) return;
      prevValueRef.current = value;

      if (!value || value.length === 0) {
        setEntries((prev) => {
          for (const e of prev) {
            if (e.objectUrl) URL.revokeObjectURL(e.objectUrl);
          }
          return [];
        });
        return;
      }

      setEntries((prev) => {
        // Revoke stale object URLs for files that are no longer in value
        for (const e of prev) {
          if (!value.includes(e.file) && e.objectUrl) {
            URL.revokeObjectURL(e.objectUrl);
          }
        }
        return value.map((file) => {
          const existing = prev.find((e) => e.file === file);
          if (existing) return existing;
          return {
            file,
            objectUrl:
              imageMode && file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            status: 'idle' as UploadStatus,
          };
        });
      });
    }, [value, imageMode]);

    // ── Process files ──────────────────────────────────────────────────────
    const processFiles = React.useCallback(
      (incoming: FileList | File[]) => {
        if (disabled) return;

        const fileArray = Array.from(incoming);
        const newErrors: string[] = [];
        const accepted: File[] = [];

        for (const file of fileArray) {
          if (accept && !fileMatchesAccept(file, accept)) {
            newErrors.push(`File type not allowed: ${file.name}`);
            continue;
          }
          const sizeMb = bytesToMb(file.size);
          if (sizeMb > maxSizeMb) {
            newErrors.push(
              `File too large: ${file.name} (${sizeMb.toFixed(1)} MB). Max is ${maxSizeMb} MB.`,
            );
            continue;
          }
          accepted.push(file);
        }

        setErrors(newErrors);

        if (accepted.length === 0) return;

        setEntries((prev) => {
          // Revoke old object URLs when replacing single-file mode
          if (!multiple) {
            for (const e of prev) {
              if (e.objectUrl) URL.revokeObjectURL(e.objectUrl);
            }
          }
          const base = multiple ? prev : [];
          const newEntries: FileEntry[] = accepted.map((file) => ({
            file,
            objectUrl:
              imageMode && file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            status: 'idle' as UploadStatus,
          }));
          const merged = [...base, ...newEntries];
          // Notify onChange immediately (no onUpload)
          if (!onUpload) {
            onChange?.(merged.map((e) => e.file));
          }
          return merged;
        });

        // Trigger upload for each accepted file if onUpload is provided
        if (onUpload) {
          for (const file of accepted) {
            setEntries((prev) =>
              prev.map((e) => (e.file === file ? { ...e, status: 'uploading' } : e)),
            );
            onUpload(file)
              .then((key) => {
                setEntries((prev) =>
                  prev.map((e) =>
                    e.file === file ? { ...e, status: 'done', uploadedKey: key } : e,
                  ),
                );
                // Notify onChange after upload completes
                setEntries((current) => {
                  onChange?.(current.map((e) => e.file));
                  return current;
                });
              })
              .catch(() => {
                setEntries((prev) =>
                  prev.map((e) => (e.file === file ? { ...e, status: 'error' } : e)),
                );
              });
          }
        }
      },
      [disabled, accept, maxSizeMb, multiple, imageMode, onUpload, onChange],
    );

    // ── Remove file ────────────────────────────────────────────────────────
    const removeEntry = React.useCallback(
      (file: File) => {
        setEntries((prev) => {
          const entry = prev.find((e) => e.file === file);
          if (entry?.objectUrl) URL.revokeObjectURL(entry.objectUrl);
          const next = prev.filter((e) => e.file !== file);
          onChange?.(next.map((e) => e.file));
          return next;
        });
      },
      [onChange],
    );

    // ── Drag handlers ──────────────────────────────────────────────────────
    const handleDragEnter = React.useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (!disabled) setIsDragging(true);
      },
      [disabled],
    );

    const handleDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDragLeave = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) setIsDragging(false);
    }, []);

    const handleDrop = React.useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        if (disabled) return;
        const files = e.dataTransfer.files;
        if (files && files.length > 0) processFiles(files);
      },
      [disabled, processFiles],
    );

    // ── Click / keyboard open ──────────────────────────────────────────────
    const openPicker = React.useCallback(() => {
      if (disabled) return;
      inputRef.current?.click();
    }, [disabled]);

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      },
      [openPicker],
    );

    const handleInputChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) processFiles(files);
        // Reset so the same file can be re-selected
        e.target.value = '';
      },
      [processFiles],
    );

    // ── Render ─────────────────────────────────────────────────────────────
    return (
      <div className={cn('w-full space-y-3', className)}>
        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          onChange={handleInputChange}
        />

        {/* Drop zone — uses div+role="button" because a <button> cannot be a valid drop target */}
        {/* biome-ignore lint/a11y/useSemanticElements: div needed for drag-and-drop; role+tabIndex+onKeyDown provide full keyboard access */}
        <div
          ref={ref}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="File upload zone. Press Enter or Space to open file picker."
          aria-disabled={disabled || undefined}
          className={cn(fileUploadZoneVariants({ isDragging, disabled }))}
          onClick={openPicker}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          {...props}
        >
          <CloudUpload
            className={cn(
              'size-10 shrink-0',
              isDragging ? 'text-primary' : 'text-muted-foreground',
            )}
            aria-hidden="true"
          />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Drag &amp; drop files here, or{' '}
              <span className="text-primary underline-offset-2 hover:underline">
                click to browse
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              {accept ? `Accepted: ${accept} · ` : ''}Max {maxSizeMb} MB per file
              {multiple ? '' : ' · Single file'}
            </p>
          </div>
        </div>

        {/* Validation errors */}
        {errors.length > 0 && (
          <ul className="space-y-1" role="alert" aria-live="polite">
            {errors.map((err) => (
              <li key={err} className="text-xs text-destructive">
                {err}
              </li>
            ))}
          </ul>
        )}

        {/* File list */}
        {entries.length > 0 && (
          <ul className="space-y-2" aria-label="Selected files">
            {entries.map((entry) => {
              const isImage = entry.file.type.startsWith('image/');
              return (
                <li
                  key={`${entry.file.name}-${entry.file.size}`}
                  className="flex items-center gap-3 rounded-[var(--radius)] border border-border bg-background px-3 py-2"
                >
                  {/* Thumbnail */}
                  {imageMode && isImage && entry.objectUrl && (
                    <img
                      src={entry.objectUrl}
                      alt={entry.file.name}
                      className="size-12 shrink-0 rounded object-cover border border-border"
                    />
                  )}

                  {/* File info */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {entry.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatBytes(entry.file.size)}</p>
                  </div>

                  {/* Upload status */}
                  {onUpload && (
                    <div className="shrink-0" aria-live="polite">
                      {entry.status === 'uploading' && (
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                          Uploading…
                        </span>
                      )}
                      {entry.status === 'done' && (
                        <CheckCircle2
                          className="size-4 text-[color:var(--color-success,oklch(0.65_0.18_145))]"
                          aria-label="Upload complete"
                        />
                      )}
                      {entry.status === 'error' && (
                        <span className="text-xs text-destructive">Failed</span>
                      )}
                    </div>
                  )}

                  {/* Remove button */}
                  <button
                    type="button"
                    aria-label={`Remove ${entry.file.name}`}
                    disabled={disabled || entry.status === 'uploading'}
                    className={cn(
                      'shrink-0 rounded p-0.5',
                      'text-muted-foreground hover:text-foreground',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'disabled:pointer-events-none disabled:opacity-40',
                      'transition-colors',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeEntry(entry.file);
                    }}
                  >
                    <X className="size-4" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  },
);

FileUploadZone.displayName = 'FileUploadZone';

export { FileUploadZone };
